import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Target, Home, BookOpen, Users, Wallet, Sparkles, Plus, Trash2, CheckCircle, Edit, Circle, Hourglass, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const objectiveTypes = [
  { value: 'immobilier', label: 'Objectif Immobilier', icon: Home, color: 'text-blue-500' },
  { value: 'formation', label: 'Objectif Formation', icon: BookOpen, color: 'text-purple-500' },
  { value: 'communaute', label: 'Objectif Communauté', icon: Users, color: 'text-green-500' },
  { value: 'financier', label: 'Objectif Financier', icon: Wallet, color: 'text-yellow-500' },
  { value: 'libre', label: 'Objectif Libre', icon: Sparkles, color: 'text-pink-500' },
];

export default function ObjectivesPage() {
  const [objectives, setObjectives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingObjective, setEditingObjective] = useState(null);
  const [proofDialog, setProofDialog] = useState({ open: false, objective: null });
  const [proofText, setProofText] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [formData, setFormData] = useState({
    objective_type: '',
    title: '',
    description: '',
    target_date: '',
  });
  const navigate = useNavigate();
  const { api } = useAuth();

  useEffect(() => {
    fetchObjectives();
  }, []);

  const fetchObjectives = async () => {
    try {
      const response = await api.get('/objectives');
      setObjectives(response.data);
    } catch (error) {
      console.error('Error fetching objectives:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await api.post('/objectives', formData);
      toast.success('Objectif créé !');
      setShowDialog(false);
      setFormData({ objective_type: '', title: '', description: '', target_date: '' });
      fetchObjectives();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la création');
    }
  };

  const updateProgress = async (objectiveId, newProgress) => {
    try {
      await api.put(`/objectives/${objectiveId}?progress=${newProgress}`);
      fetchObjectives();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const markAsCompleted = async (objectiveId) => {
    const objective = objectives.find(o => o.id === objectiveId);
    setProofText('');
    setProofDialog({ open: true, objective });
  };

  const submitProof = async () => {
    const objective = proofDialog.objective;
    if (!objective) return;
    if (proofText.trim().length < 60) {
      toast.error('Explique en 2-3 phrases comment tu as atteint cet objectif (60 caractères minimum).');
      return;
    }
    setVerifying(true);
    try {
      const res = await api.put(`/objectives/${objective.id}?is_completed=true`, { proof_text: proofText.trim() });
      if (res.data.status === 'approved') {
        toast.success(res.data.message);
      } else {
        toast.info(res.data.message);
      }
      setProofDialog({ open: false, objective: null });
      fetchObjectives();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
    } finally {
      setVerifying(false);
    }
  };

  const markAsNotCompleted = async (objectiveId) => {
    try {
      await api.put(`/objectives/${objectiveId}?is_completed=false`);
      toast.success('Objectif remis "à réaliser"');
      fetchObjectives();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const deleteObjective = async (objectiveId) => {
    try {
      await api.delete(`/objectives/${objectiveId}`);
      toast.success('Objectif supprimé');
      fetchObjectives();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const getObjectiveIcon = (type) => {
    const found = objectiveTypes.find(t => t.value === type);
    return found ? found.icon : Target;
  };

  const getObjectiveColor = (type) => {
    const found = objectiveTypes.find(t => t.value === type);
    return found ? found.color : 'text-primary';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Target className="w-12 h-12 text-primary animate-pulse" />
      </div>
    );
  }

  const completedCount = objectives.filter(o => o.is_completed).length;

  return (
    <div className="min-h-screen bg-background" data-testid="objectives-page">
      <div className="max-w-4xl mx-auto p-4 sm:p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold">Mes Objectifs</h1>
              <p className="text-muted-foreground">Définissez jusqu'à 5 objectifs personnels</p>
            </div>
          </div>
          
          {objectives.length < 5 && (
            <Dialog open={showDialog} onOpenChange={setShowDialog}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="w-4 h-4 mr-2" />
                  Nouvel objectif
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Créer un objectif</DialogTitle>
                  <DialogDescription>
                    Définissez un objectif clair et atteignable
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="text-sm font-medium">Type d'objectif</label>
                    <Select 
                      value={formData.objective_type} 
                      onValueChange={(value) => setFormData({...formData, objective_type: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Choisir un type" />
                      </SelectTrigger>
                      <SelectContent>
                        {objectiveTypes.map(type => (
                          <SelectItem key={type.value} value={type.value}>
                            <div className="flex items-center gap-2">
                              <type.icon className={`w-4 h-4 ${type.color}`} />
                              {type.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Titre</label>
                    <Input
                      value={formData.title}
                      onChange={(e) => setFormData({...formData, title: e.target.value})}
                      placeholder="Ex: Acheter mon premier bien d'ici 6 mois"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Description (optionnel)</label>
                    <Textarea
                      value={formData.description}
                      onChange={(e) => setFormData({...formData, description: e.target.value})}
                      placeholder="Détails supplémentaires..."
                      rows={3}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Date cible (optionnel)</label>
                    <Input
                      type="date"
                      value={formData.target_date}
                      onChange={(e) => setFormData({...formData, target_date: e.target.value})}
                    />
                  </div>
                  <Button type="submit" className="w-full">
                    Créer l'objectif
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Progress Summary */}
        <Card className="mb-6 bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="font-semibold">Progression globale</h3>
                <p className="text-sm text-muted-foreground">
                  {completedCount} objectif(s) atteint(s) sur {objectives.length}
                </p>
              </div>
              <div className="text-3xl font-bold text-primary">
                {objectives.length > 0 ? Math.round((completedCount / objectives.length) * 100) : 0}%
              </div>
            </div>
            <Progress value={objectives.length > 0 ? (completedCount / objectives.length) * 100 : 0} className="h-3" />
          </CardContent>
        </Card>

        {/* Objectives List */}
        {objectives.length > 0 ? (
          <div className="space-y-4">
            {objectives.map(objective => {
              const Icon = getObjectiveIcon(objective.objective_type);
              const color = getObjectiveColor(objective.objective_type);
              
              return (
                <Card 
                  key={objective.id} 
                  className={`${objective.is_completed ? 'bg-green-500/10 border-green-500/30' : ''}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-full flex items-center justify-center ${objective.is_completed ? 'bg-green-500/30' : 'bg-secondary'}`}>
                        {objective.is_completed ? (
                          <CheckCircle className="w-6 h-6 text-green-500" />
                        ) : (
                          <Icon className={`w-6 h-6 ${color}`} />
                        )}
                      </div>
                      
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <h3 className="font-semibold">{objective.title}</h3>
                          <div className="flex items-center gap-2">
                            {objective.pending_validation && !objective.is_completed && (
                              <Badge variant="outline" className="gap-1 text-xs" data-testid={`objective-pending-${objective.id}`}>
                                <Hourglass className="w-3 h-3" />
                                Vérification en cours
                              </Badge>
                            )}
                            {objective.validated_by === 'ai' && objective.is_completed && (
                              <Badge variant="outline" className="gap-1 text-xs">
                                <Sparkles className="w-3 h-3" />
                                Validé par IA
                              </Badge>
                            )}
                            <Badge variant={objective.is_completed ? 'default' : 'secondary'}>
                              {objectiveTypes.find(t => t.value === objective.objective_type)?.label || 'Objectif'}
                            </Badge>
                          </div>
                        </div>
                        
                        {objective.description && (
                          <p className="text-sm text-muted-foreground mb-2">{objective.description}</p>
                        )}
                        
                        {objective.target_date && (
                          <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                            <Target className="w-3 h-3" />
                            Échéance : {new Date(objective.target_date).toLocaleDateString('fr-FR')}
                          </p>
                        )}
                        
                        {!objective.is_completed && (
                          <div className="flex items-center gap-2 mt-3">
                            <Progress value={objective.progress} className="h-2 flex-1" />
                            <span className="text-sm font-medium w-12 text-right">{objective.progress}%</span>
                          </div>
                        )}
                        
                        {!objective.is_completed && (
                          <div className="flex items-center gap-2 mt-3">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => updateProgress(objective.id, Math.min(100, objective.progress + 10))}
                            >
                              +10%
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => updateProgress(objective.id, Math.min(100, objective.progress + 25))}
                            >
                              +25%
                            </Button>
                            <Button 
                              size="sm"
                              onClick={() => markAsCompleted(objective.id)}
                              className="ml-auto"
                              disabled={objective.pending_validation}
                              data-testid={`complete-objective-${objective.id}`}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              {objective.pending_validation ? 'En attente' : 'Terminer'}
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => deleteObjective(objective.id)}
                              className="text-red-500 hover:text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                        
                        {objective.is_completed && (
                          <div className="flex items-center gap-2 mt-3">
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="border-orange-500/50 text-orange-500 hover:bg-orange-500/10"
                              onClick={() => markAsNotCompleted(objective.id)}
                            >
                              <Circle className="w-4 h-4 mr-1" />
                              Remettre à réaliser
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon"
                              onClick={() => deleteObjective(objective.id)}
                              className="text-red-500 hover:text-red-600 ml-auto"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        ) : (
          <Card>
            <CardContent className="p-12 text-center">
              <Target className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="text-lg font-semibold mb-2">Aucun objectif défini</h3>
              <p className="text-muted-foreground mb-4">
                Définissez vos objectifs pour suivre votre progression et gagner des XP !
              </p>
              <Button onClick={() => setShowDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Créer mon premier objectif
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Tips */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              Conseils pour vos objectifs
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>• Soyez <strong>spécifique</strong> : "Acheter un T2 à Lyon" plutôt que "Acheter un bien"</li>
              <li>• Fixez une <strong>date limite</strong> réaliste pour vous motiver</li>
              <li>• Découpez les grands objectifs en <strong>étapes intermédiaires</strong></li>
              <li>• Complétez un objectif pour gagner <strong>+100 XP</strong> !</li>
              <li>• À la validation, décrivez <strong>comment</strong> vous l'avez atteint : une analyse assistée par IA vérifie la cohérence avant de créditer les XP</li>
            </ul>
          </CardContent>
        </Card>
      </div>

      {/* Objective completion proof (AI audited) */}
      <Dialog
        open={proofDialog.open}
        onOpenChange={(open) => setProofDialog({ open, objective: open ? proofDialog.objective : null })}
      >
        <DialogContent className="max-w-lg" data-testid="objective-proof-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-primary" />
              Comment as-tu atteint cet objectif ?
            </DialogTitle>
            <DialogDescription>
              « {proofDialog.objective?.title} » vaut 100 XP. Décris en 2-3 phrases concrètes ce que tu as fait
              (dates, chiffres, actions). Une analyse assistée par IA vérifie la cohérence : si tout est clair,
              les XP sont créditées immédiatement.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Textarea
              rows={6}
              value={proofText}
              onChange={(e) => setProofText(e.target.value)}
              placeholder="Ex : J'ai signé le compromis le 12 mai pour le T2 rue Gambetta à 88 500 €, financement validé au Crédit Agricole le 28 mai, remise des clés prévue fin juin."
              data-testid="objective-proof-input"
            />
            <div className="flex items-center justify-between text-xs">
              <span className={proofText.trim().length < 60 ? 'text-muted-foreground' : 'text-primary'}>
                {proofText.trim().length}/60 caractères minimum
              </span>
              <span className="flex items-center gap-1 text-muted-foreground">
                <Sparkles className="w-3 h-3" />
                Analyse assistée par IA
              </span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setProofDialog({ open: false, objective: null })}>
              Annuler
            </Button>
            <Button onClick={submitProof} disabled={verifying} data-testid="objective-proof-submit">
              {verifying ? 'Vérification en cours...' : 'Valider mon objectif'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
